import { useState, useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useScrollAnimation, staggerContainer, fadeInUp } from '../../hooks/useScrollAnimation';
import ServiceCard from '../ui/ServiceCard';
import BranchWatermark from '../ui/BranchWatermark';
import { services } from '../../data/services';
import { useLang } from '../context/LanguageContext';

const POSES = {
  center: { x: '0%', scale: 1, opacity: 1 },
  right: { x: '70%', scale: 0.82, opacity: 0.45 },
  left: { x: '-70%', scale: 0.82, opacity: 0.45 },
  hidden: { x: '0%', scale: 0.7, opacity: 0 },
};
const Z_INDEX = { center: 3, right: 2, left: 2, hidden: 1 };
const SWIPE_THRESHOLD = 60;

const ServicesSection = ({ id }) => {
  const { ref, inView } = useScrollAnimation();
  const { t } = useLang();
  const wordPairs = t.services.wordPairs;
  const pairCount = wordPairs.length;
  const [pairIndex, setPairIndex] = useState(0);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (reduceMotion) return undefined;
    const interval = setInterval(() => {
      setPairIndex((prev) => (prev + 1) % pairCount);
    }, 3500);
    return () => clearInterval(interval);
  }, [pairCount, reduceMotion]);

  const mergedServices = services.map((service, index) => ({
    ...service,
    title: t.services.cards[index].title,
    bullets: t.services.cards[index].bullets,
  }));

  const total = mergedServices.length;
  const [active, setActive] = useState(0);
  const next = () => setActive((a) => (a + 1) % total);
  const prev = () => setActive((a) => (a - 1 + total) % total);

  const poseFor = (index) => {
    const offset = (index - active + total) % total;
    if (offset === 0) return 'center';
    if (offset === 1) return 'right';
    if (offset === total - 1) return 'left';
    return 'hidden';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowRight') next();
    if (e.key === 'ArrowLeft') prev();
  };

  return (
    <section id={id} className="section-padding bg-cream relative overflow-hidden" ref={ref}>
      <img
        src="/images/fondos/hoja.jpg"
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
      <div className="absolute inset-0 bg-cream/85 pointer-events-none" />
      <BranchWatermark style={{ top: '-30px', right: '-20px', height: '42vh', opacity: 0.14, transform: 'rotate(150deg)' }} />
      <BranchWatermark style={{ top: 'auto', bottom: '-30px', left: '-20px', right: 'auto', height: '36vh', opacity: 0.12, transform: 'rotate(-25deg) scaleX(-1)' }} />

      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="container-max relative z-10"
      >
        <div className="text-center mb-6">
          <motion.span
            variants={fadeInUp}
            className="inline-block px-6 py-2.5 rounded-full text-base md:text-lg font-medium bg-primary text-cream border border-primary"
          >
            {t.services.badge}
          </motion.span>
        </div>

        <div className="text-center mb-8">
          <motion.h2
            variants={fadeInUp}
            className="font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-primary"
          >
            {t.services.titleLine1}
          </motion.h2>
          <div className="h-16 md:h-20 flex items-center justify-center overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.span
                key={pairIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-secondary italic"
              >
                {wordPairs[pairIndex % pairCount]}
              </motion.span>
            </AnimatePresence>
          </div>
        </div>

        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <p className="text-gray-600 font-body text-base leading-relaxed">{t.services.description1}</p>
          <p className="text-gray-600 font-body text-base leading-relaxed">{t.services.description2}</p>
          <p className="text-gray-600 font-body text-base leading-relaxed">{t.services.description3}</p>
        </motion.div>

        <motion.div
          variants={fadeInUp}
          role="region"
          aria-roledescription={t.services.carousel}
          aria-label={t.services.badge}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          className="outline-none focus-visible:ring-2 focus-visible:ring-accent/40 rounded-2xl"
        >
          <div className="grid justify-items-center">
            {mergedServices.map((service, i) => {
              const pose = poseFor(i);
              const isCenter = pose === 'center';
              return (
                <motion.div
                  key={service.id}
                  className="[grid-area:1/1] w-[85vw] max-w-md"
                  style={{
                    zIndex: Z_INDEX[pose],
                    pointerEvents: pose === 'hidden' ? 'none' : 'auto',
                    cursor: isCenter ? 'grab' : 'pointer',
                  }}
                  initial={false}
                  animate={POSES[pose]}
                  transition={{ type: 'spring', stiffness: 260, damping: 30 }}
                  aria-hidden={!isCenter}
                  onClick={() => !isCenter && setActive(i)}
                  drag={isCenter ? 'x' : false}
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.25}
                  onDragEnd={(_, info) => {
                    if (info.offset.x < -SWIPE_THRESHOLD) next();
                    else if (info.offset.x > SWIPE_THRESHOLD) prev();
                  }}
                  whileDrag={{ cursor: 'grabbing' }}
                >
                  <ServiceCard service={service} />
                </motion.div>
              );
            })}
          </div>

          <div className="flex justify-center items-center gap-4 mt-10">
            <button
              onClick={prev}
              className="w-11 h-11 rounded-full bg-white border border-primary/15 shadow-soft flex items-center justify-center hover:border-accent transition-colors"
              aria-label={t.services.prev}
            >
              <ChevronLeft size={20} className="text-primary" aria-hidden="true" />
            </button>
            <div className="flex gap-2">
              {mergedServices.map((service, i) => (
                <button
                  key={service.id}
                  onClick={() => setActive(i)}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    i === active ? 'bg-accent w-6' : 'bg-primary/20 w-2.5'
                  }`}
                  aria-label={`${t.services.goTo} ${i + 1}: ${service.title}`}
                  aria-current={i === active}
                />
              ))}
            </div>
            <button
              onClick={next}
              className="w-11 h-11 rounded-full bg-white border border-primary/15 shadow-soft flex items-center justify-center hover:border-accent transition-colors"
              aria-label={t.services.next}
            >
              <ChevronRight size={20} className="text-primary" aria-hidden="true" />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default ServicesSection;
